import axios, { AxiosError, AxiosResponse } from 'axios';
import { actions, AppStore } from '@/store';
import TokenManager from './token-manager';

const BASE_URL = window.location.origin;

let retryNo = 0;
let isTokenRefreshing = false;
const RETRY_DEFAULT_WAIT = 100;
const RETRY_BACKOFF_TIME = 300;
const MAX_RETRY_NO = 5;
const refreshSubscribers: any[] = [];

const onTokenRefreshed = () => {
  while (refreshSubscribers.length) {
    const callback = refreshSubscribers.shift();
    if (callback) {
      callback();
    }
  }
};

const addRefreshSubscriber = (callback: any) => {
  refreshSubscribers.push(callback);
};

const fetcher = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
});

fetcher.interceptors.request.use(
  (config) => {
    const { at } = TokenManager.getToken();
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${at}`,
    };
    return config;
  },
  (error) => Promise.reject(error),
);

fetcher.interceptors.response.use(
  async (response: AxiosResponse) => {
    const originalRequest = response.config;
    if (response && response.status === 202) {
      const { error } = response.data;
      if (error && error.message === 'token is expired') {
        if (retryNo > MAX_RETRY_NO) {
          return Promise.reject(new Error('failed to renew token'));
        }

        if (!isTokenRefreshing) {
          isTokenRefreshing = true;

          await new Promise((resolve) => setTimeout(resolve, RETRY_DEFAULT_WAIT + retryNo * RETRY_BACKOFF_TIME));

          retryNo += 1;

          const refreshToken = TokenManager.getToken().rt;
          const { data } = await axios.get(`${BASE_URL}/api/auth/token/renew`, {
            headers: { Authorization: `Bearer ${refreshToken}` },
          });

          if (data.message === 'Unauthorized Access') {
            TokenManager.removeToken();

            retryNo = 0;
            return { data: {} };
          }

          if (data.message === 'login again') { // refresh token is expired
            TokenManager.removeToken();

            retryNo = 0;
            return { data: {} };
          }

          const {
            at: newAt,
            rt: newRt,
          } = data.data;
          TokenManager.setToken({ at: newAt, rt: newRt });
          isTokenRefreshing = false;
          const retryOriginalRequest = new Promise((resolve) => {
            addRefreshSubscriber(() => resolve(fetcher(originalRequest)));
          });
          onTokenRefreshed();
          return retryOriginalRequest;
        }

        const retryOriginalRequest = new Promise((resolve) => {
          addRefreshSubscriber(() => resolve(fetcher(originalRequest)));
        });
        return retryOriginalRequest;
      }

      if (response.data.message === 'Unauthorized Access') {
        AppStore.dispatch(actions.common.userInfo.initProfile());
      }
    }
    return response;
  },
  // eslint-disable-next-line arrow-body-style
  async (error: AxiosError) => {
    // TODO: error handling

    return Promise.reject(error);
  },
);

export default fetcher;
